import {
  CASTING_MOVE_SCALE,
  CROUCH_SPEED,
  FATIGUE_RUN_RESUME,
  FLY_CLIMB_SPEED,
  FLY_SPEED,
  GHOST_SPEED,
  GRAVITY,
  GROUND_STICK_SPEED,
  IMPULSE_DECAY_PER_SEC,
  JUMP_VELOCITY,
  RUN_SPEED,
  SPREAD_AIRBORNE,
  SPREAD_CROUCHED,
  SPREAD_RUNNING,
  SPREAD_STILL,
  SPREAD_WALKING,
  STAND_HEIGHT,
  WALK_SPEED,
  FLY_PP_RESUME_FRACTION,
} from '../constants.ts';
import { CLASSES } from '../classes.ts';
import { moveVector, type Vec3 } from '../math.ts';
import { insideBarrier } from '../placed.ts';
import { isRooted, type PlayerState } from '../player.ts';
import type { PlayerInput, SimState, WorldQuery } from './state.ts';

/**
 * Movement: running, stance, spread, and walking a body through the world.
 *
 * `movePlayer` is the one function both ends of a hosted match must run
 * identically — the room to decide where somebody is, a client to predict it.
 * Collision itself is the world's business, through `WorldQuery.moveBody`;
 * core only says where a body is trying to go.
 */

/**
 * How wide this mage's next shot will scatter.
 *
 * Derived from the input rather than from measured velocity, because the server
 * receives the same inputs and has to reach the same number — and because core
 * does not integrate movement at all, so it has no velocity to measure.
 */
export function spreadFor(p: PlayerState, input: PlayerInput): number {
  if (p.flying || !p.onGround) return SPREAD_AIRBORNE;
  if (p.crouching) return SPREAD_CROUCHED;

  const moving = Math.abs(input.moveForward) > 0.01 || Math.abs(input.moveRight) > 0.01;
  if (!moving) return SPREAD_STILL;
  return p.running ? SPREAD_RUNNING : SPREAD_WALKING;
}

/**
 * Whether the mage is running this tick.
 *
 * The exhaustion latch is the whole point. `fatigue > 0` on its own oscillates:
 * the tick you stop running regenerates enough fatigue to run for two more,
 * which empties it again, so speed and the reticle strobed several times a
 * second at the bottom of the bar. Once empty, the bar has to come back up to
 * FATIGUE_RUN_RESUME before the legs work again.
 */
export function resolveRunning(p: PlayerState, input: PlayerInput): void {
  if (p.fatigue <= 0) p.exhausted = true;
  else if (p.fatigue >= FATIGUE_RUN_RESUME) p.exhausted = false;

  const moving = Math.abs(input.moveForward) > 0.01 || Math.abs(input.moveRight) > 0.01;
  p.running = input.run && moving && !p.exhausted && !p.flying && !p.ghost;
}

/**
 * Walks a mage that nothing else is walking.
 *
 * The local player's position arrives from Rapier by way of the renderer's
 * character controller, and there is only one of those. Everyone else — every
 * bot, and every remote player once a server exists — has to be moved here, in
 * core, or they aim and fire from wherever they spawned for the whole match.
 *
 * Deliberately cruder than the controller: no capsule, no autostep, no slopes.
 * A blocked step is retried on each axis alone so a bot slides along a wall
 * rather than sticking to it, and that plus a navigation grid that already keeps
 * them off the walls is enough. Anything more would mean core owning collision,
 * which is the thing the whole architecture is arranged to avoid.
 */
/**
 * Exported so a client can predict with it.
 *
 * The one function both ends must run identically: a room runs it to decide
 * where somebody actually is, and a client runs it to guess before the answer
 * comes back. Two implementations would be a stream of corrections — a mage that
 * stutters constantly, for no reason anybody could see.
 */
export function movePlayer(
  p: PlayerState, input: PlayerInput, world: WorldQuery, dt: number,
): void {
  // Ghosts move too. They drift toward a Cleric hoping to be called back, and a
  // ghost that cannot move is a ghost that waits where it fell for the rest of
  // the match.
  if (!p.alive && !p.ghost) return;

  /*
   * An impulse, taken up before anything else this tick.
   *
   * Core used to raise these and the renderer used to consume them, which meant
   * a Sign of Expulsion threw the local player and merely inconvenienced every
   * bot in the game. Consumed here, they throw everybody.
   */
  const impulse = p.pendingImpulse;
  if (impulse) {
    p.pendingImpulse = null;
    p.verticalVelocity = impulse.y;
    p.vel.x = impulse.x;
    p.vel.z = impulse.z;
    // Being launched ends a hover, or flight would cancel the arc outright.
    p.flying = false;
  }

  const base = p.ghost
    ? GHOST_SPEED
    : p.flying
      ? FLY_SPEED
      : p.crouching
        ? CROUCH_SPEED
        : p.running ? RUN_SPEED : WALK_SPEED;
  // Ghosts are not slowed by anything; nothing they pass through is solid.
  const speed = p.ghost ? base : base * p.moveScale;
  const move = moveVector(p.yaw, input.moveForward, input.moveRight);

  /*
   * External velocity decays exponentially, so a launch carries and then lets
   * go rather than stopping dead in the air.
   */
  const decay = Math.pow(IMPULSE_DECAY_PER_SEC, dt);
  p.vel.x *= decay;
  p.vel.z *= decay;
  if (Math.abs(p.vel.x) < 0.01) p.vel.x = 0;
  if (Math.abs(p.vel.z) < 0.01) p.vel.z = 0;

  const flying = p.flying || p.ghost;

  /*
   * What gravity, a jump or a hover is asking for vertically.
   *
   * All of this lived in the renderer, which is why a bot could be marked as
   * flying and stay on the floor, and why only one mage in the match ever
   * jumped. The rules are the same for everybody now; only the *resolution*
   * differs, and that is the world's business rather than the simulation's.
   */
  let dy: number;
  if (flying) {
    p.verticalVelocity = 0;
    const climbRate = FLY_CLIMB_SPEED * p.moveScale;
    dy = ((input.flyUp ? 1 : 0) - (input.flyDown ? 1 : 0)) * climbRate * dt;
  } else if (p.onGround && p.verticalVelocity <= 0) {
    /*
     * A root reaches the jump, and only the simulation knows whether there is
     * one. `moveScale` gates the horizontal step and nothing else, so a
     * paralysed mage kept full jump velocity and hopped on the spot — they
     * landed where they took off, so nothing was gained, but "rooted in place"
     * and hopping in place are different claims.
     */
    const mayJump = input.jump && !isRooted(p);
    // Held against the floor rather than zeroed, or a mage asking to move
    // nowhere vertically reports as airborne every other frame and bobs.
    p.verticalVelocity = mayJump ? JUMP_VELOCITY : -GROUND_STICK_SPEED;
    dy = p.verticalVelocity * dt;
  } else {
    p.verticalVelocity -= GRAVITY * dt;
    dy = p.verticalVelocity * dt;
  }

  const delta = {
    x: move.x * speed * dt + p.vel.x * dt,
    y: dy,
    z: move.z * speed * dt + p.vel.z * dt,
  };

  /*
   * A real body, where there is one.
   *
   * The renderer supplies a capsule and a character controller; a headless
   * server or a test supplies nothing and gets the cruder resolution below.
   * Both are asked the same question and both answer in world coordinates,
   * which is what lets one mage be driven by Rapier and the next by a ray
   * without the simulation knowing or caring which.
   */
  const landed = world.moveBody?.(p, delta, dt);
  if (landed) {
    p.pos = { ...landed.pos };
    p.onGround = landed.grounded;
    if (landed.grounded && p.verticalVelocity < 0) p.verticalVelocity = 0;
    return;
  }

  const dx = delta.x;
  const dz = delta.z;
  const chest = (x: number, z: number): Vec3 => ({ x, y: p.pos.y + 0.9, z });
  const from = chest(p.pos.x, p.pos.z);

  if (dx !== 0 || dz !== 0) {
    if (!world.segmentBlocked(from, chest(p.pos.x + dx, p.pos.z + dz))) {
      p.pos.x += dx;
      p.pos.z += dz;
    } else if (dx !== 0 && !world.segmentBlocked(from, chest(p.pos.x + dx, p.pos.z))) {
      // Slide: whichever single axis is still clear.
      p.pos.x += dx;
    } else if (dz !== 0 && !world.segmentBlocked(from, chest(p.pos.x, p.pos.z + dz))) {
      p.pos.z += dz;
    }
  }

  /*
   * Flight, which core could set but could not perform.
   *
   * `p.flying` was raised here and drained fatigue here, but the only code that
   * ever moved a mage upward lived in the renderer's character controller — and
   * that runs for the local player alone. So a bot that pressed the key paid the
   * fatigue, was marked as flying, and stayed resolutely on the floor. The
   * Mentalist's defining ability worked for exactly one mage in the match.
   *
   * Vertical only: the horizontal step above is shared with everybody, because a
   * flying mage still walks about, only faster and off the ground.
   */
  if (flying) {
    const climb = dy;
    if (climb !== 0) {
      const head = (y: number): Vec3 => ({ x: p.pos.x, y, z: p.pos.z });
      if (!world.segmentBlocked(head(p.pos.y + 1.6), head(p.pos.y + 1.6 + climb))) {
        p.pos.y += climb;
      }
    }
    p.onGround = false;
    return;
  }

  settle(p, world, dt);
}

/**
 * Drops a mage onto whatever is under them.
 *
 * Core moved bots on the horizontal plane only, so every one of them kept
 * whatever height it spawned at for the whole match — which is why they stood in
 * the air at exactly dais height, having been placed on a shrine and never
 * having come down. The renderer's character controller does this for the local
 * player and there is only one of those.
 *
 * A ray straight down rather than a swept body: the mage's footprint is already
 * kept off the walls by the navigation grid, and a full character controller in
 * core would mean core owning collision, which is the thing the architecture is
 * arranged to avoid.
 */
export function settle(p: PlayerState, world: WorldQuery, dt: number): void {
  // Flight is the one case where being off the ground is the point.
  if (p.flying) {
    p.onGround = false;
    return;
  }

  /*
   * Rising: leave them alone and let the arc finish.
   *
   * `settle` snaps a mage to whatever is under them, which is right for walking
   * and wrong for anything that has just been thrown. A Sign of Expulsion set
   * the velocity, this pinned them back to the floor on the same tick, and the
   * launch that is the whole point of the spell happened to nobody the renderer
   * was not already moving.
   */
  if (p.verticalVelocity > 0) {
    p.pos.y += p.verticalVelocity * dt;
    p.verticalVelocity -= GRAVITY * dt;
    p.onGround = false;
    return;
  }

  // From head height, so a mage standing inside a step is pushed up onto it
  // rather than falling through the floor they are already intersecting.
  const from = { x: p.pos.x, y: p.pos.y + STAND_HEIGHT, z: p.pos.z };
  const hit = world.raycast(from, { x: 0, y: -1, z: 0 }, STAND_HEIGHT + 40);
  // Nothing underneath is not the same as a floor at zero. Valhalla is forty
  // metres down, so falling back to zero would have hauled anybody walking it
  // straight up into the arena — and any future arena with a pit would do the
  // same. With no answer, leave them where they are.
  if (!hit) {
    p.onGround = true;
    return;
  }
  const floor = hit.point.y;

  const gap = p.pos.y - floor;
  if (gap <= 0.05) {
    // Standing. Held a little into the floor rather than exactly on it, for the
    // same reason the controller does: contact that is only just true flickers.
    p.pos.y = floor;
    p.verticalVelocity = 0;
    p.onGround = true;
    return;
  }

  p.verticalVelocity -= GRAVITY * dt;
  p.pos.y += p.verticalVelocity * dt;
  if (p.pos.y <= floor) {
    p.pos.y = floor;
    p.verticalVelocity = 0;
    p.onGround = true;
  } else {
    p.onGround = false;
  }
}

/**
 * Slowest thing acting on the player wins, whether that is a wall they are
 * standing in or a root they are under. Recomputed from nothing every tick so
 * that a wall expiring underfoot — or a root running out — cannot leave someone
 * permanently slowed.
 */
export function computeMoveScale(s: SimState, p: PlayerState): number {
  let scale = 1;
  for (const b of s.barriers) {
    if (b.passSlow >= scale) continue;
    if (!insideBarrier(b, p.pos)) continue;
    scale = b.passSlow;
  }
  for (const e of p.effects) {
    if (e.slow === undefined || e.slow >= scale) continue;
    scale = e.slow;
  }
  // The whole price of a wind-up. Slowest thing wins, so a cast inside an
  // Airwall is not slower than either of them alone.
  if (p.casting && CASTING_MOVE_SCALE < scale) scale = CASTING_MOVE_SCALE;
  return scale;
}

// ── What a tick does to movement, for the room and the prediction alike ──────

/** The look, taken from the input before anything moves. */
export function steer(p: PlayerState, input: PlayerInput): void {
  p.yaw = input.yaw;
  p.pitch = input.pitch;
}

/**
 * Whether a mage is flying after this tick: the fatigue and power latches, the
 * key, and whatever grounds them. Settled after the move, for the next one.
 */
export function intendFlight(p: PlayerState, input: PlayerInput): void {
  if (p.pp <= 0) p.drained = true;
  else if (p.pp >= p.ppMax * FLY_PP_RESUME_FRACTION) p.drained = false;
  // A root grounds a Mentalist as surely as it stops a Magician walking —
  // otherwise Paralysis is a spell that only works on three of the classes.
  if (CLASSES[p.cls].canFly && input.flyUp && p.fatigue > 0 && !p.drained && !isRooted(p)) {
    p.flying = true;
  }
  if (isRooted(p)) p.flying = false;
  if (input.flyDown && p.onGround) p.flying = false;
}

/**
 * Not conditioned on being grounded. It once was, and grounded flickers on and
 * off by a frame at a time as the character controller re-derives it, which
 * made a crouching mage bob up and down while they walked. Flight is what the
 * same key means in the air, so flight is what has to exclude it.
 */
export function intendCrouch(p: PlayerState, input: PlayerInput): void {
  p.crouching = input.crouch && !p.flying && !p.ghost;
}

/**
 * One tick of a single mage's movement, exactly as `step` performs it, for a
 * client predicting its own mage.
 *
 * Prediction used to call `movePlayer` alone — which moves along `p.yaw`, and
 * nothing set `p.yaw` but `step`. So the predicted mage walked in whatever
 * direction the *last snapshot* said it faced, a round trip stale, and every
 * turn was predicted wrong until the server's answer arrived and pulled it
 * round: rubber-banding on every change of direction, worst for a ghost or a
 * flier turning hard. The same was true of running, crouching and taking off.
 * `step` now calls the same pieces in the same order, so the two cannot drift.
 */
export function predictTick(p: PlayerState, input: PlayerInput, world: WorldQuery, dt: number): void {
  steer(p, input);
  movePlayer(p, input, world, dt);
  if (p.ghost || !p.alive) return;
  resolveRunning(p, input);
  intendFlight(p, input);
  intendCrouch(p, input);
}
